import '@/styles/calendar.css';
import { useMemo, useState } from 'react';
import type { Location, View } from '@shared/types';
import { isCompleteType } from '@shared/constants';
import { getDb, useDb, useMe } from '@/lib/store';
import { statusOf } from '@/lib/hierarchy';
import { updateViewSettings } from '@/lib/actions';
import { toast } from '@/components/ui/overlays';
import { ViewToolbar } from './ViewToolbar';
import { useViewQuery } from './useViewQuery';
import { CalendarDnd } from './calendar/CalendarDnd';
import { CalendarNav } from './calendar/CalendarNav';
import { MonthGrid } from './calendar/MonthGrid';
import { TimeGrid, useNow } from './calendar/TimeGrid';
import { SidePanel, SideStrip, type PanelKind } from './calendar/SidePanel';
import { dayOf, isUnscheduled, quickCreate, rangeTitle, resolveListId, shiftCursor, visibleDays, type AddTarget, type CalendarMode } from './calendar/calendarUtils';

export function CalendarView({ view, location }: { view: View; location: Location }) {
  const [search, setSearch] = useState('');
  const mode: CalendarMode = view.settings.calendarMode ?? 'month';
  const setMode = (m: CalendarMode) => updateViewSettings(view.id, { calendarMode: m });
  const now = useNow();
  const today = dayOf(now);
  const [cursor, setCursor] = useState(() => dayOf(Date.now()));
  const [adding, setAdding] = useState<AddTarget | null>(null);
  const [panel, setPanel] = useState<PanelKind | null>(null);
  const me = useMe();

  const q = useViewQuery(view, location, search, { flatten: true });
  const tasks = q.matched;
  const { scheduled, unscheduled, overdue } = useMemo(() => {
    const db = getDb();
    const scheduled = tasks.filter((t) => !isUnscheduled(t));
    const unscheduled = tasks.filter(isUnscheduled).sort((a, b) => a.orderIndex - b.orderIndex);
    const overdue = tasks
      .filter((t) => t.dueDate !== null && t.dueDate < today && !isCompleteType(statusOf(db, t).type))
      .sort((a, b) => a.dueDate! - b.dueDate!);
    return { scheduled, unscheduled, overdue };
  }, [tasks, today]);

  const days = useMemo(() => (mode === 'month' ? [] : visibleDays(mode, cursor)), [mode, cursor]);
  const listId = useDb((db) => resolveListId(db, location));
  const create = (name: string, target: AddTarget) => {
    if (!listId) {
      toast('Create a list first to add tasks here');
      return;
    }
    quickCreate(listId, name, target, view.settings.meMode && me ? [me] : []);
    setAdding(null);
  };
  const openDay = (d: number) => {
    setCursor(d);
    setMode('day');
  };

  return (
    <div className="cal">
      <ViewToolbar
        view={view}
        location={location}
        search={search}
        onSearch={setSearch}
        features={{ group: false, subtasks: false, columns: false, sort: false }}
        onAddTask={() => {
          setCursor(today);
          setAdding({ day: today, minutes: null });
        }}
        leftExtra={
          <CalendarNav
            mode={mode}
            modes={['month', 'week', 'day']}
            onMode={(m) => {
              setAdding(null);
              setMode(m);
            }}
            title={rangeTitle(mode, cursor)}
            onToday={() => setCursor(today)}
            onPrev={() => (setAdding(null), setCursor((c) => shiftCursor(mode, c, -1)))}
            onNext={() => (setAdding(null), setCursor((c) => shiftCursor(mode, c, 1)))}
          />
        }
      />
      <CalendarDnd>
        <div className="cal-body">
          <div className="cal-main">
            {mode === 'month' ? (
              <MonthGrid cursor={cursor} tasks={scheduled} today={today} adding={adding} onAdd={setAdding} onCreate={create} onOpenDay={openDay} />
            ) : (
              <TimeGrid key={mode} days={days} tasks={scheduled} today={today} adding={adding} onAdd={setAdding} onCreate={create} onOpenDay={openDay} />
            )}
          </div>
          {panel && <SidePanel kind={panel} unscheduled={unscheduled} overdue={overdue} onKind={setPanel} onClose={() => setPanel(null)} />}
          <SideStrip open={panel} unscheduled={unscheduled.length} overdue={overdue.length} onToggle={setPanel} />
        </div>
      </CalendarDnd>
    </div>
  );
}
